import { ref, computed } from "vue";

/**
 * パスワードポリシーの正規表現（8文字以上、英大文字・英小文字・数字・記号を各1つ以上含む）
 * Cognitoの記号: ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \ , > < ' : ; | _ ~ + =
 * スペースは先頭・末尾以外で記号として扱われる（別途バリデーション）
 */
const PASSWORD_REGEX = new RegExp(
  "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[\\^$*.\\[\\]{}()?\\-\"!@#%&/\\\\,><':;|_~+= ]).{8,}$"
);

/** 先頭・末尾スペースチェック用正規表現 */
const LEADING_TRAILING_SPACE_REGEX = /^\s|\s$/;

/** パスワードポリシーのエラーメッセージ */
const PASSWORD_POLICY_MESSAGE =
  "パスワードには少なくとも1つの半角英大文字、半角英小文字、数字、記号を含めてください。";

/** 先頭・末尾スペースのエラーメッセージ */
const LEADING_TRAILING_SPACE_MESSAGE =
  "パスワードの先頭または末尾にスペースを使用することはできません。";

/**
 * パスワード変更 フォーム状態管理を扱う composable。
 * フォーム入力状態、バリデーション、エラーメッセージ管理を行う。
 */
export const usePasswordChangeForm = () => {
  // フォーム入力値
  const currentPassword = ref<string>("");
  const newPassword = ref<string>("");
  const confirmNewPassword = ref<string>("");

  // タッチ状態（フォーカスアウト後に必須チェックを表示するため）
  const currentPasswordTouched = ref<boolean>(false);
  const newPasswordTouched = ref<boolean>(false);
  const confirmNewPasswordTouched = ref<boolean>(false);

  /**
   * フォームのバリデーションを実行する。
   * @returns エラーメッセージ。エラーがなければ null を返す。
   */
  function validate(): string | null {
    // 現在のパスワードが入力されているか
    if (currentPassword.value.length === 0) {
      return "現在のパスワードを入力してください。";
    }

    // 新しいパスワードが入力されているか
    if (newPassword.value.length === 0) {
      return "新しいパスワードを入力してください。";
    }

    // 先頭・末尾スペースチェック
    if (LEADING_TRAILING_SPACE_REGEX.test(newPassword.value)) {
      return LEADING_TRAILING_SPACE_MESSAGE;
    }

    // パスワードポリシーチェック
    if (!PASSWORD_REGEX.test(newPassword.value)) {
      return PASSWORD_POLICY_MESSAGE;
    }

    // 新しいパスワードと確認用パスワードが一致しているか
    if (newPassword.value !== confirmNewPassword.value) {
      return "新しいパスワードと確認用パスワードが一致しません。";
    }

    // 現在のパスワードと新しいパスワードが異なるか
    if (currentPassword.value === newPassword.value) {
      return "新しいパスワードは現在のパスワードと異なるものを入力してください。";
    }

    return null;
  }

  /**
   * 全項目をタッチ済みにする（送信ボタン押下時など）。
   */
  function touchAll(): void {
    currentPasswordTouched.value = true;
    newPasswordTouched.value = true;
    confirmNewPasswordTouched.value = true;
  }

  /**
   * フォームの入力値をクリアする。
   */
  function clearForm(): void {
    currentPassword.value = "";
    newPassword.value = "";
    confirmNewPassword.value = "";
    currentPasswordTouched.value = false;
    newPasswordTouched.value = false;
    confirmNewPasswordTouched.value = false;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // リアルタイムバリデーション（入力時に各項目のエラーメッセージを表示）
  ////////////////////////////////////////////////////////////////////////////////

  /** 現在のパスワード入力欄のエラーメッセージ */
  const currentPasswordErrors = computed<string[]>(() => {
    // タッチ済み かつ 未入力の場合は必須エラーを表示
    if (currentPasswordTouched.value && currentPassword.value.length === 0) {
      return ["現在のパスワードは必須項目です。"];
    }
    return [];
  });

  /** 新しいパスワード入力欄のエラーメッセージ */
  const newPasswordErrors = computed<string[]>(() => {
    // タッチ済み かつ 未入力の場合は必須エラーを表示
    if (newPasswordTouched.value && newPassword.value.length === 0) {
      return ["新しいパスワードは必須項目です。"];
    }
    // 未入力の場合はエラー表示しない（初期状態）
    if (newPassword.value.length === 0) return [];

    const errors: string[] = [];

    // 先頭・末尾スペースチェック
    if (LEADING_TRAILING_SPACE_REGEX.test(newPassword.value)) {
      errors.push(LEADING_TRAILING_SPACE_MESSAGE);
    }

    // パスワードポリシーチェック
    if (!PASSWORD_REGEX.test(newPassword.value)) {
      errors.push(PASSWORD_POLICY_MESSAGE);
    }

    // 現在のパスワードと同一チェック
    if (
      currentPassword.value.length > 0 &&
      currentPassword.value === newPassword.value
    ) {
      errors.push(
        "新しいパスワードは現在のパスワードと異なるものを入力してください。"
      );
    }

    return errors;
  });

  /** 新しいパスワード（確認）入力欄のエラーメッセージ */
  const confirmNewPasswordErrors = computed<string[]>(() => {
    // タッチ済み かつ 未入力の場合は必須エラーを表示
    if (confirmNewPasswordTouched.value && confirmNewPassword.value.length === 0) {
      return ["新しいパスワード（確認）は必須項目です。"];
    }
    // 未入力の場合はエラー表示しない（初期状態）
    if (confirmNewPassword.value.length === 0) return [];

    const errors: string[] = [];

    // 新しいパスワードとの一致チェック
    if (newPassword.value !== confirmNewPassword.value) {
      errors.push("新しいパスワードと確認用パスワードが一致しません。");
    }

    return errors;
  });

  /**
   * フォーム全体のバリデーション状態。
   * すべての項目が入力済みかつエラーがない場合に true を返す。
   */
  const isFormValid = computed<boolean>(() => {
    // すべての項目が入力済みであること
    if (currentPassword.value.length === 0) return false;
    if (newPassword.value.length === 0) return false;
    if (confirmNewPassword.value.length === 0) return false;

    // 各項目にエラーがないこと
    if (newPasswordErrors.value.length > 0) return false;
    if (confirmNewPasswordErrors.value.length > 0) return false;

    return true;
  });

  return {
    currentPassword,
    newPassword,
    confirmNewPassword,
    currentPasswordTouched,
    newPasswordTouched,
    confirmNewPasswordTouched,
    currentPasswordErrors,
    newPasswordErrors,
    confirmNewPasswordErrors,
    isFormValid,
    touchAll,
    validate,
    clearForm,
  };
};
